import React, { useState, MouseEvent, useCallback, useEffect } from "react";

import styles from "./styles.module.scss";
import { IoClose } from "react-icons/io5";
import { ITimePeriod, PeriodChooser, StructureHeader } from "src/main";
import {
  IGlobalPeriodContext,
  IStructureGrouping,
  usePortfolioAllocation,
} from "@iliotech/data-wire";
import { useParams } from "react-router-dom";
import { PortfolioDTOWithDetailsV3RiskAssetViewEnum } from "@iliotech/generated-api-v3";
import { observer } from "mobx-react-lite";

export interface ReportCellSettingsDropdownProps {
  handleClose: () => void;
  periodContext?: IGlobalPeriodContext;
  structureContext?: IStructureGrouping;
  onPeriodSelected: (period: ITimePeriod) => void;
  setGrouping: (grouping: string) => void;
  setSelectedGroup: (group: { code: string; label: string }) => void;
  initialPosition: { x: number; y: number };
  inceptionDateOverride?: string;
}

export const ReportCellSettingsDropdown: React.FC<ReportCellSettingsDropdownProps> =
  observer(
    ({
      handleClose,
      onPeriodSelected,
      periodContext,
      structureContext,
      setGrouping,
      setSelectedGroup,
      initialPosition,
      inceptionDateOverride,
    }) => {
      const [position, setPosition] = useState(initialPosition);
      const [isDragging, setIsDragging] = useState(false);
      const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

      const PeriodChooserObserver = observer(PeriodChooser);
      const selectedPeriod = {
        from: periodContext?.period?.from,
        to: periodContext?.period?.to,
      };

      const handleMouseMove = useCallback(
        (e: MouseEvent) => {
          if (isDragging) {
            setPosition({
              x: e.clientX - dragOffset.x,
              y: e.clientY - dragOffset.y,
            });
          }
        },
        [isDragging, dragOffset]
      );

      const handleMouseUp = useCallback(() => {
        setIsDragging(false);
      }, []);

      const handleMouseDown = (e: MouseEvent) => {
        setIsDragging(true);
        setDragOffset({
          x: e.clientX - position.x,
          y: e.clientY - position.y,
        });
      };

      useEffect(() => {
        if (isDragging) {
          //@ts-ignore
          window.addEventListener("mousemove", handleMouseMove);
          window.addEventListener("mouseup", handleMouseUp);
        }

        return () => {
          //@ts-ignore
          window.removeEventListener("mousemove", handleMouseMove);
          window.removeEventListener("mouseup", handleMouseUp);
        };
      }, [isDragging, handleMouseMove, handleMouseUp]);

      const { portfolioId } = useParams();
      const allocations = usePortfolioAllocation(portfolioId!);

      return (
        <div
          style={{
            position: "absolute",
            left: `${position.x}px`,
            top: `${position.y}px`,
            cursor: isDragging ? "grabbing" : "grab",
          }}
          className={styles.dropdown}
        >
          <div onMouseDown={handleMouseDown} className={styles.header}>
            <span>Cell Settings</span>
            <IoClose className={styles.close} onClick={handleClose} />
          </div>
          <PeriodChooserObserver
            periodContext={periodContext}
            epochDate={periodContext?.epochDate!}
            inceptionDate={inceptionDateOverride}
            onPeriodSelected={onPeriodSelected}
            selectedPeriod={selectedPeriod}
            hideCustomDate
            hidePeriodString
            reduced
          />
          <div className={styles.structureHeaderWrapper}>
            <StructureHeader
              riskAssetView={
                PortfolioDTOWithDetailsV3RiskAssetViewEnum.RiskAssetClass
              }
              structureContext={structureContext}
              {...allocations?.data?.data}
              onGroupingSelect={setGrouping}
              onGroupSelect={setSelectedGroup}
            />
          </div>
        </div>
      );
    }
  );
